/** @jsxImportSource @emotion/react */
import { useState, useRef, useEffect, ReactNode } from "react";
import { css } from "@emotion/react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { type ChartType } from "./metrics/ChartTypeRenderer";
import { InfoTooltip } from "@phoenix/components";

// ── Chart type metadata ────────────────────────────────────────────────────────
interface ChartTypeOption {
  type: ChartType;
  label: string;
  icon: string;
  group: "Total Value";
}

const CHART_OPTIONS: ChartTypeOption[] = [
  { type: "vertical_bar", label: "Vertical Bar Chart", icon: "▌▌▌", group: "Total Value" },
  { type: "horizontal_bar", label: "Horizontal Bar Chart", icon: "≡", group: "Total Value" },
  { type: "line", label: "Line Chart", icon: "〜", group: "Total Value" },
  { type: "pie", label: "Pie Chart", icon: "◕", group: "Total Value" },
  { type: "big_number", label: "Big Number", icon: "#", group: "Total Value" },
];

// ── Styles ─────────────────────────────────────────────────────────────────────
const widgetCSS = css`
  background: var(--ac-global-background-color-dark);
  border: 1px solid var(--ac-global-color-grey-200);
  border-radius: 10px;
  padding: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  min-height: 320px;
  transition: box-shadow 0.2s;
  position: relative;
  &:hover .widget-toolbar {
    opacity: 1;
  }
`;

const widgetDraggingCSS = css`
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.35);
  opacity: 0.85;
  z-index: 999;
`;

const widgetHeaderCSS = css`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 14px 14px 0 14px;
  gap: 8px;
`;

const widgetTitleAreaCSS = css`
  flex: 1;
  min-width: 0;
`;

const widgetTitleCSS = css`
  font-size: 13px;
  font-weight: 600;
  color: var(--ac-global-text-color-900);
  margin-bottom: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const widgetSubCSS = css`
  font-size: 11px;
  color: var(--ac-global-text-color-500);
`;

const toolbarCSS = css`
  display: flex;
  align-items: center;
  gap: 4px;
  opacity: 0;
  transition: opacity 0.15s;
  flex-shrink: 0;
`;

const toolBtnCSS = css`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 6px;
  border: none;
  background: transparent;
  cursor: pointer;
  color: var(--ac-global-text-color-500);
  font-size: 14px;
  transition: background 0.1s, color 0.1s;
  &:hover {
    background: var(--ac-global-color-grey-100);
    color: var(--ac-global-text-color-900);
  }
`;

const dragHandleCSS = css`
  cursor: grab;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 6px;
  color: var(--ac-global-text-color-400);
  font-size: 14px;
  transition: background 0.1s, color 0.1s;
  &:hover {
    background: var(--ac-global-color-grey-100);
    color: var(--ac-global-text-color-700);
  }
  &:active {
    cursor: grabbing;
  }
`;

const widgetBodyCSS = css`
  flex: 1 1 auto;
  min-height: 240px;
  padding: 4px 14px 14px 14px;
  position: relative;
`;

// ── Dropdown ───────────────────────────────────────────────────────────────────
const dropdownCSS = css`
  position: absolute;
  top: 42px;
  right: 10px;
  background: var(--ac-global-background-color-dark);
  border: 1px solid var(--ac-global-color-grey-200);
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.25);
  z-index: 1000;
  padding: 6px;
  min-width: 200px;
`;

const dropdownGroupLabelCSS = css`
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: var(--ac-global-text-color-400);
  padding: 6px 8px 4px 8px;
`;

const dropdownItemCSS = (active: boolean) => css`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 7px 10px;
  border-radius: 7px;
  cursor: pointer;
  font-size: 12px;
  color: ${active ? "var(--ac-global-text-color-900)" : "var(--ac-global-text-color-700)"};
  background: ${active ? "var(--ac-global-color-grey-100)" : "transparent"};
  font-weight: ${active ? "600" : "400"};
  transition: background 0.1s;
  &:hover {
    background: var(--ac-global-color-grey-100);
    color: var(--ac-global-text-color-900);
  }
`;

const dropdownIconCSS = css`
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 700;
  color: var(--ac-global-text-color-600);
  flex-shrink: 0;
`;

const checkmarkCSS = css`
  margin-left: auto;
  color: var(--ac-global-color-primary);
  font-size: 14px;
`;

// ── Props ──────────────────────────────────────────────────────────────────────
interface DashboardWidgetProps {
  id: string;
  title: string;
  subtitle: string;
  chartType: ChartType;
  supportsTypeSwitch?: boolean;
  onChartTypeChange: (type: ChartType) => void;
  onDownloadCSV?: () => void;
  info?: string;
  children: ReactNode;
}

export function DashboardWidget({
  id,
  title,
  subtitle,
  chartType,
  supportsTypeSwitch = true,
  onChartTypeChange,
  onDownloadCSV,
  info,
  children,
}: DashboardWidgetProps) {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    }
    if (dropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [dropdownOpen]);

  return (
    <div
      ref={setNodeRef}
      style={style}
      css={[widgetCSS, isDragging && widgetDraggingCSS]}
    >
      {/* Header */}
      <div css={widgetHeaderCSS}>
        <div css={widgetTitleAreaCSS}>
          <div css={css`display: flex; align-items: center; gap: 6px;`}>
            <div css={widgetTitleCSS}>{title}</div>
            {info && <InfoTooltip content={info} />}
          </div>
          <div css={widgetSubCSS}>{subtitle}</div>
        </div>

        {/* Toolbar — appears on hover */}
        <div css={toolbarCSS} className="widget-toolbar">
          {/* Chart type picker */}
          {supportsTypeSwitch && (
            <div style={{ position: "relative" }} ref={dropdownRef}>
              <button
                css={toolBtnCSS}
                title="Change chart type"
                onClick={() => setDropdownOpen((v) => !v)}
              >
                📊
              </button>
              {dropdownOpen && (
                <div css={dropdownCSS}>
                  <div css={dropdownGroupLabelCSS}>Total Value</div>
                  {CHART_OPTIONS.map((opt) => (
                    <div
                      key={opt.type}
                      css={dropdownItemCSS(chartType === opt.type)}
                      onClick={() => {
                        onChartTypeChange(opt.type);
                        setDropdownOpen(false);
                      }}
                    >
                      <span css={dropdownIconCSS}>{opt.icon}</span>
                      <span>{opt.label}</span>
                      {chartType === opt.type && (
                        <span css={checkmarkCSS}>✓</span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Download CSV button */}
          {onDownloadCSV && (
            <button
              css={toolBtnCSS}
              title="Download CSV"
              onClick={onDownloadCSV}
            >
              📥
            </button>
          )}

          {/* Drag handle */}
          <div
            css={dragHandleCSS}
            title="Drag to reorder"
            {...attributes}
            {...listeners}
          >
            ⠿
          </div>
        </div>
      </div>

      {/* Chart body */}
      <div css={widgetBodyCSS}>
        {children}
      </div>
    </div>
  );
}
